/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name align max-line-length */

import {ProjectElement}           from './project.element';
import {ProjectElementType}       from './project.element.type';
import {Project}                  from '../project';

import {Blob as FileBlob, BlobType} from '../blob';

import {Logger}                   from '@/util/logger';

import {JSONBrowserMetadata}      from '@/screens/ide/json.browser.metadata';

import {first}                    from "rxjs/operators";
import {basename, empty} from "@/util/util";
import {ProjectElementFactoryDecorator} from "@/models/project/project.element.factory.decorator";
import {FileBrowserMetadata} from "@/screens/ide/file.browser.metadata";
import {PropertiesHolder} from "@/models/properties.holder";

/**
 *
 * @class PropertyElement
 *
 * @brief node to use for hanging properties off of other elements in the tree.
 * Properties are just simple name/value items that are useful for the user to see
 * directly in the project manager, rather than having to dig around to see them.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      September 2024
 *
 */
@ProjectElementFactoryDecorator('PropertyElement')
export class PropertyElement extends ProjectElement {

  /** the property name*/
  public _name: string = '';

  /** the property value */
  public _value: string = '';

  /** if you need a set of values for the property you can use this data map */
  public _data: Map<string,any> = new Map<string,any>;


  /** standard constructor */
  constructor(
    project:  Project,
    id:       string,
    name:     string,
    value:    string,
    data:     Map<string,any>,
    parent:   ProjectElement,
    publish?: boolean,
    peType?:  ProjectElementType,
    icon?:    string
  ) {

    super(project, id, peType ? peType : ProjectElementType.Property);

    this._name        = name;
    this._value       = value;
    this._data        = data;
    this._enabled     = true;
    this._label       = this.generateLabel();
    this._icon        = icon;
    this._elementKind = 'Property';

    /* normal child add...*/

    if (parent) {

      this._project.addElement(this, parent, publish);
    }

  }

  private generateLabel(): string {

    return this._name;
  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType(): void {
    console.log("requiring PropertyElement");
  }

  /*
   * when blobs get moved/copied in bulk, project elements have to allow being told where
   * their blobs have been moved to.
   *
   */
  public adjusted(searchKey: string, replaceKey: string) {

    super.adjusted(searchKey, replaceKey);
  }

  /** fetch any secondary blobs for this element */
  public secondaryBlobs(): string[] {

    return [];
  }

  /** is this element deletable? */
  public isDeletable() : boolean {

    return true;
  }

  /** test if any blob of this type can be a child of this element */
  public acceptsBlobType(type: BlobType): boolean {

    return false;
  }

  /** convert this element to a simplified form that can be easily serialized */
  public node(init?: any) {

    return super.node({
      name:  this._name,
      value: this._value,
      data:  Object.fromEntries(this._data),
      icon:  this._icon
    });
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any): void {

    /* recover the basics */

    super.fromNode(node);

    /* recover the job details */

    this._name  = node.name;
    this._value = node.value;
    this._data  = new Map<string,any>(Object.entries(node.data));
    this._icon  = node.icon;

    /* make sure our dynamic label is correct */

    this._label     = this.generateLabel();
  }


  /** append these elements properties to the holder */
  public properties(properties?: PropertiesHolder): PropertiesHolder {

    if (!properties) {

      properties = new PropertiesHolder();

    }

    properties.set('Kind',   this._elementKind);
    properties.set('ID',     this._id);
    properties.set('Name',   this._name);
    properties.set('Value',  this._value);

    for (let [k,v] of this._data.entries()) {

      if (typeof v != 'string') {

        properties.set(k, v.toString());

      } else {

        properties.set(k, v);
      }
    }

    /* add in the usual properties */

    return super.properties(properties);
  }

}
